import { acceptHMRUpdate, defineStore } from 'pinia'
import type {
  AddressStep,
  NewCollector,
  NewCook,
  NewFoodie,
  NewMatchRequest,
  RegisterCaretakerParams,
  RegisterCollectorParams,
  RegisterCookParams,
  RegisterFoodieParams,
  RegisterMatchRequestParams,
  RegistrationFormProgress,
  SearchAddressResponse
} from '~/types/registration'
import { CaretakerOptions, PromotionsEnum } from '~/types/registration'
import type { MaybeArray, RecursivePartial } from '~/types/utils'
import { removeWhitespaceFromString } from '~/utils/helpers'
import { cloneDeep } from 'es-toolkit'
import type { Token } from '~/types/auth'
import statusMessages from '/@lang/status-messages.json'
import type { SignupReference, SignupReferenceId } from '~/types/signup-references'
import type { OptionsMap } from '~/types/elementsUi'
import {
  SignupReferenceNewspaperCategory,
  SignupReferenceOrganisationCategory,
  SignupReferenceThirdPartySocialMediaCategory
} from '~/models/signupReferences'
import type { ApiResponse } from '#shared/types/utils'
import type { UserType } from '#shared/types/users.ts'
import { usePersist } from '~/plugins/pinia.ts'

interface State {
  signupReferences?: SignupReference[]
  foodie: NewFoodie
  cook: NewCook
  matchRequest: NewMatchRequest
  collector: NewCollector
  formProgress: {
    foodie: RegistrationFormProgress
    cook: RegistrationFormProgress
    collector: RegistrationFormProgress
    matchRequest: RegistrationFormProgress
  }
}

const registrationStatusMessages = statusMessages['registration-status-messages']

const baseNewFoodie: RecursivePartial<NewFoodie> = {
  step: 'address',

  addressStep: {
    postalCode: undefined,
    houseNumber: undefined,
    reasonOption: undefined,
    reasonText: null
  },
  mealWishesStep: {
    amountOfDays: 1,
    amountOfPeople: 1,
    generalMealWishes: [],
    specificMealWishes: [],
    miscMealWish: null,
    helpCookDescription: undefined
  },
  contactStep: {
    firstName: undefined,
    lastName: undefined,
    gender: undefined,
    email: undefined,
    noEmail: false,
    mailUpdatesAccepted: true,
    phoneNumber: undefined,
    signupReferenceId: undefined,
    password: undefined,
    agreeToTermsAndConditions: false
  },
  caretakerType: CaretakerOptions.Personal,
  caretakerOptions: undefined,

  addressButtonName: 'buttons.search-address',
  addressSearchResult: undefined,
  matchRequestCount: undefined,

  signUpPopup: 'close',
  isPraatjePot: false,
  isVoorproefje: false
}

const baseNewCook: RecursivePartial<NewCook> = {
  step: 'personal-details',

  personalDetailsStep: {
    firstName: undefined,
    lastName: undefined,
    postalCode: undefined,
    houseNumber: undefined,
    email: undefined,
    mailUpdatesAccepted: true,
    phoneNumber: undefined,
    gender: undefined,
    signupReferenceId: undefined,
    password: undefined,
    birthdate: undefined
  },
  cookingAbilitiesStep: {
    generalMealWishes: [],
    specificMealWishes: [],
    miscMealWish: null,
    kitchenPreferences: [],
    miscKitchenPreference: null,
    description: undefined
  },
  cookConsentStep: {
    VOGConsent: false,
    agreedToHygieneGuidelinesAt: false,
    agreeToTermsAndConditions: false
  },
  addressSearchResult: undefined,
  matchRequestCount: undefined,

  isKookmeer: false
}

const baseNewCollector: RecursivePartial<NewCollector> = {
  step: 'register',
  personalDetailSteps: {
    firstName: undefined,
    lastName: undefined,
    postalCode: undefined,
    houseNumber: undefined,
    email: undefined,
    mailUpdatesAccepted: false,
    phoneNumber: undefined,
    password: undefined
  },
  addressSearchResult: undefined
}

const baseNewMatchRequest: RecursivePartial<NewMatchRequest> = {
  mealWishesStep: {
    amountOfDays: 1,
    amountOfPeople: 1,
    helpCookDescription: undefined
  }
}

export const useRegistrationStore = defineStore('registration', {
  state: (): State => ({
    signupReferences: undefined,
    foodie: cloneDeep(baseNewFoodie as NewFoodie),
    cook: cloneDeep(baseNewCook as NewCook),
    collector: cloneDeep(baseNewCollector as NewCollector),
    matchRequest: cloneDeep(baseNewMatchRequest as NewMatchRequest),
    formProgress: {
      foodie: {
        isConfirmStep: false,
        hasSubmitted: false,
        hasFiredEvents: false,
        municipality: null
      },
      cook: {
        isConfirmStep: false,
        hasSubmitted: false,
        hasFiredEvents: false,
        municipality: null
      },
      collector: {
        isConfirmStep: false,
        hasSubmitted: false
      },
      matchRequest: {
        isConfirmStep: false,
        hasSubmitted: false
      }
    }
  }),
  persist: usePersist<State>([
    {
      pick: ['signupReferences', 'cook', 'foodie', 'collector', 'matchRequest'],
      storage: piniaPluginPersistedstate.localStorage()
    },
    {
      pick: ['formProgress'],
      storage: piniaPluginPersistedstate.cookies()
    }
  ]),
  logout: true,
  actions: {
    resetState() {
      this.$reset()
    },

    setIsPraatjePot(isActive: boolean = false) {
      this.foodie.isPraatjePot = isActive
    },

    setIsVoorproefje(isActive: boolean = false) {
      this.foodie.isVoorproefje = isActive
    },

    setIsKookmeer(isActive: boolean = false) {
      this.cook.isKookmeer = isActive
    },

    async getSignupReferences(
      roleType?: UserType,
      municipality?: string,
      postalCode?: string,
      houseNumber?: string
    ): Promise<boolean> {
      try {
        const response = await this.api.post<ApiResponse<SignupReference[]>>(
          '/signup-references',
          {
            municipality,
            postalCode,
            houseNumber,
            roleType
          }
        )

        this.signupReferences = response.data
        return true
      } catch {
        return false
      }
    },

    async searchHouseNumbers(
      postalCode: string,
      houseNumber: string
    ): Promise<MaybeArray<string> | false> {
      try {
        const response = await this.api.post<ApiResponse<string[]>>(
          '/search/autocomplete',
          {
            houseNumber,
            postalCode
          }
        )

        const houseNumbers = response.data

        if (houseNumbers.length > 1) {
          return houseNumbers
        }
        if (houseNumbers.length === 1) {
          return houseNumbers[0]
        }
        return false
      } catch {
        return false
      }
    },

    async fetchFullAddress(userType: UserType, address: AddressStep): Promise<boolean> {
      const form = this[userType]

      if (
        (userType === 'foodie' && !this.foodie.addressStep) ||
        (userType === 'cook' && !address) ||
        (userType === 'collector' && !address)
      ) {
        return false
      }

      const currentPostal = removeWhitespaceFromString(
        form.addressSearchResult?.postalCode
      )?.toLowerCase()
      const currentHouseNumber = removeWhitespaceFromString(
        form.addressSearchResult?.houseNumber
      )
      const incomingPostal = removeWhitespaceFromString(address.postalCode)?.toLowerCase()
      const incomingHouseNumber = removeWhitespaceFromString(address.houseNumber)

      if (
        currentPostal === incomingPostal &&
        currentHouseNumber === incomingHouseNumber
      ) {
        return true
      }

      try {
        const response = await this.api.post<SearchAddressResponse>('/search/address', {
          ...address,
          userType: userType === 'collector' ? null : userType
        })

        const addressData = response.address

        if (!addressData || Array.isArray(addressData)) {
          return false
        }

        form.addressSearchResult = addressData

        if (userType === 'foodie' || userType === 'cook') {
          this[userType].matchRequestCount = response.matchRequestCount
        }

        this.signupReferences = response.signupReferences

        return true
      } catch {
        return false
      }
    },

    async registerCook(): Promise<boolean> {
      if (!this.cookParams) {
        return false
      }

      const params = cloneDeep(this.cookParams)
      params.promotions = this.cookPromotions

      try {
        const response = await this.api.post<ApiResponse<Token>>(
          'registration/cook',
          params
        )
        this.cook = cloneDeep(baseNewCook as NewCook)
        useAuthStore().onLogin(response.data)
        return true
      } catch {
        addToast().error(
          registrationStatusMessages[useLocaleStore().locale].error['register-cook']
        )
        return false
      }
    },

    async registerFoodie(): Promise<boolean> {
      if (!this.foodieParams) {
        return false
      }

      const params = cloneDeep(this.foodieParams)
      params.promotions = this.foodiePromotions

      try {
        const response = await this.api.post<ApiResponse<Token>>(
          'registration/foodie',
          params
        )
        this.foodie = cloneDeep(baseNewFoodie as NewFoodie)
        useAuthStore().onLogin(response.data)
        return true
      } catch {
        addToast().error(
          registrationStatusMessages[useLocaleStore().locale].error['register-foodie']
        )
        return false
      }
    },

    async registerCollectant(): Promise<boolean> {
      if (!this.collectorParams) {
        return false
      }

      try {
        const response = await this.api.post<ApiResponse<Token>>(
          'registration/collector',
          this.collectorParams
        )

        this.collector = cloneDeep(baseNewCollector as NewCollector)
        this.formProgress.collector.hasSubmitted = true
        useAuthStore().onLogin(response.data)
        return true
      } catch {
        addToast().error(
          registrationStatusMessages[this.currentLocale].error['register-collector']
        )
        return false
      }
    },

    async registerMatchRequest(): Promise<boolean> {
      if (!this.matchRequestParams) {
        addToast().error(
          registrationStatusMessages[this.currentLocale].error['register-match-request']
        )
        return false
      }

      try {
        await this.api.post('match-requests/new', this.matchRequestParams)
        this.matchRequest = cloneDeep(baseNewMatchRequest as NewMatchRequest)

        addToast().success(
          registrationStatusMessages[this.currentLocale].success['register-match-request']
        )
        return true
      } catch {
        addToast().error(
          registrationStatusMessages[this.currentLocale].error['register-match-request']
        )
        return false
      }
    }
  },
  getters: {
    signupReferenceOptions: (state: State): OptionsMap[] => {
      const localeIsDutch = useLocaleStore().localeIsDutch

      if (state.signupReferences) {
        const references = state.signupReferences
          .filter((s) => !s.category)
          .map<OptionsMap>((reference: SignupReference) => {
            return {
              id: reference.id,
              name: localeIsDutch ? reference.reference : reference.translation
            }
          })
          .toSorted((a, b) => a.name.localeCompare(b.name))

        const referencesWithCategory = Object.groupBy(
          state.signupReferences,
          (s) => s.category ?? 'unassigned'
        )

        const organisationReferences =
          referencesWithCategory[SignupReferenceOrganisationCategory]
        if (organisationReferences && organisationReferences.length > 0) {
          references.unshift({
            id: SignupReferenceOrganisationCategory,
            name: localeIsDutch
              ? 'Zorg- en Welzijnsorganisaties'
              : 'Healthcare and Welfare Organizations'
          })
        }

        const thirdPartySocialMediaReferences =
          referencesWithCategory[SignupReferenceThirdPartySocialMediaCategory]
        if (
          thirdPartySocialMediaReferences &&
          thirdPartySocialMediaReferences.length > 0
        ) {
          references.unshift({
            id: SignupReferenceThirdPartySocialMediaCategory,
            name: localeIsDutch
              ? 'Social media van iemand anders'
              : "Someone else's social media"
          })
        }

        const newspaperReferences =
          referencesWithCategory[SignupReferenceNewspaperCategory]
        if (newspaperReferences && newspaperReferences.length > 0) {
          references.unshift({
            id: SignupReferenceNewspaperCategory,
            name: localeIsDutch ? 'Krantenartikel' : 'Newspaper Article'
          })
        }
        return references
      } else {
        return []
      }
    },

    signupReferenceCategoryOptions:
      (state: State) =>
      (category?: string | SignupReferenceId): OptionsMap[] => {
        const localeIsDutch = useLocaleStore().localeIsDutch

        if (typeof category !== 'string') {
          return []
        }

        if (state.signupReferences) {
          return state.signupReferences
            .filter((s) => s.category === category)
            .map<OptionsMap>((reference: SignupReference) => {
              return {
                id: reference.id,
                name: localeIsDutch ? reference.reference : reference.translation
              }
            })
            .toSorted((a, b) => a.name.localeCompare(b.name))
        } else {
          return []
        }
      },

    cookPromotions: (state: State): string[] | null => {
      const collectedPromotions: PromotionsEnum[] = []

      if (state.cook.isKookmeer) {
        collectedPromotions.push(PromotionsEnum.Kookmeer)
      }

      let promotions: PromotionsEnum[] | null = null
      if (collectedPromotions.length > 0) {
        promotions = collectedPromotions
      }

      return promotions
    },
    foodiePromotions: (state: State): string[] | null => {
      const collectedPromotions: PromotionsEnum[] = []

      if (state.foodie.isPraatjePot) {
        collectedPromotions.push(PromotionsEnum.PraatjePot)
      }

      if (state.foodie.isVoorproefje) {
        collectedPromotions.push(PromotionsEnum.Voorproefje)
      }

      if (state.foodie.signUpPopup === 'sign-up') {
        collectedPromotions.push(PromotionsEnum.FoodiePopUp)
      }

      let promotions: PromotionsEnum[] | null = null
      if (collectedPromotions.length > 0) {
        promotions = collectedPromotions
      }

      return promotions
    },
    cookParams: (state: State): RegisterCookParams | undefined => {
      if (
        !state.formProgress.cook.isConfirmStep ||
        state.formProgress.cook.hasSubmitted
      ) {
        return undefined
      }

      const cook = state.cook

      const addressData = cook.addressSearchResult

      if (!addressData) {
        return undefined
      }

      return {
        // Personal details step
        firstname: cook.personalDetailsStep.firstName,
        lastname: cook.personalDetailsStep.lastName,
        email: cook.personalDetailsStep.email,
        mailUpdatesAccepted: cook.personalDetailsStep.mailUpdatesAccepted,
        phone: cook.personalDetailsStep.phoneNumber,
        gender: cook.personalDetailsStep.gender,
        password: cook.personalDetailsStep.password,
        birthdate: cook.personalDetailsStep.birthdate,

        ...addressData,
        postalCode: addressData.postalCode, // Need these two explicitly due to variable names from elastic search
        houseNumber: addressData.houseNumber,

        signupReferenceId: cook.personalDetailsStep.signupReferenceId,
        signupReasonId: cook.personalDetailsStep.signupReasonId,

        // Cooking abilities step
        generalMealWishes: cook.cookingAbilitiesStep.generalMealWishes,
        specificMealWishes: cook.cookingAbilitiesStep.specificMealWishes,
        miscMealWish: cook.cookingAbilitiesStep.miscMealWish,
        kitchenPreferences: cook.cookingAbilitiesStep.kitchenPreferences,
        miscKitchenPreference: cook.cookingAbilitiesStep.miscKitchenPreference,

        description: cook.cookingAbilitiesStep.description,
        vogConsent: cook.cookConsentStep.VOGConsent,
        agreedToHygieneGuidelinesAt: cook.cookConsentStep.agreedToHygieneGuidelinesAt,

        // Preferred language
        locale: useLocaleStore().locale,

        // Promotions
        promotions: null
      }
    },
    foodieParams: (state: State): RegisterFoodieParams | undefined => {
      if (
        !state.formProgress.foodie.isConfirmStep ||
        state.formProgress.foodie.hasSubmitted
      ) {
        return undefined
      }

      const foodie = state.foodie

      const addressData = foodie.addressSearchResult

      if (!addressData) {
        return undefined
      }

      let caretaker: RegisterCaretakerParams | undefined = undefined

      if (
        state.foodie.caretakerType === CaretakerOptions.Caretaker &&
        state.foodie.caretakerOptions
      ) {
        const caretakerForm = state.foodie.caretakerOptions
        caretaker = {
          firstName: caretakerForm.firstName,
          lastName: caretakerForm.lastName,
          email: caretakerForm.email,
          phoneNumber: caretakerForm.phoneNumber,
          institute: caretakerForm.institute,
          notes: caretakerForm.notes,
          type: caretakerForm.type,
          contactForIntake: caretakerForm.contactForIntake,
          contactForImpact: caretakerForm.contactForImpact
        }
      }

      return {
        // Address step
        reasonOption: foodie.addressStep.reasonOption,
        reasonText: foodie.addressStep.reasonText ?? null,

        caretaker: caretaker,

        ...addressData,
        postalCode: addressData.postalCode, // Need these two explicitly due to variable names from elastic search
        houseNumber: addressData.houseNumber,

        // Meal wishes step
        timesAWeek: foodie.mealWishesStep.amountOfDays,
        amountOfPeople: foodie.mealWishesStep.amountOfPeople,
        helpCookDescription: foodie.mealWishesStep.helpCookDescription,

        generalMealWishes: foodie.mealWishesStep.generalMealWishes,
        specificMealWishes: foodie.mealWishesStep.specificMealWishes.filter(
          (mealWish) => typeof mealWish === 'number'
        ), //Remove 'misc'
        miscMealWish: foodie.mealWishesStep.miscMealWish,

        // Contact step
        firstname: foodie.contactStep.firstName,
        lastname: foodie.contactStep.lastName,
        gender: foodie.contactStep.gender,
        email: foodie.contactStep.noEmail ? undefined : foodie.contactStep.email,
        mailUpdatesAccepted: foodie.contactStep.mailUpdatesAccepted,
        phone: foodie.contactStep.phoneNumber,
        signupReferenceId: foodie.contactStep.signupReferenceId,
        password: foodie.contactStep.password,

        // Preferred language
        locale: useLocaleStore().locale,

        // Promotions
        promotions: null
      }
    },
    collectorParams: (state: State): RegisterCollectorParams | undefined => {
      if (state.formProgress.collector.hasSubmitted) {
        return undefined
      }

      const form = state.collector
      const addressData = form.addressSearchResult
      if (!addressData) {
        return
      }

      return {
        firstname: form.personalDetailSteps.firstName,
        lastname: form.personalDetailSteps.lastName,
        phone: form.personalDetailSteps.phoneNumber,
        email: form.personalDetailSteps.email,
        locale: useLocaleStore().locale,
        password: form.personalDetailSteps.password,
        ...addressData
      }
    },
    matchRequestParams: (state: State): RegisterMatchRequestParams | undefined => {
      if (
        !state.formProgress.matchRequest.isConfirmStep ||
        state.formProgress.matchRequest.hasSubmitted
      ) {
        return undefined
      }

      const mealWishesStep = state.matchRequest.mealWishesStep

      return {
        timesAWeek: mealWishesStep.amountOfDays,
        amountOfPeople: mealWishesStep.amountOfPeople,
        helpCookDescription: mealWishesStep.helpCookDescription
      }
    }
  }
})

/**
 * Pinia supports Hot Module replacement, so you can edit your stores and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRegistrationStore, import.meta.hot))
}
